import { concat, Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';
import { catchError, map, mergeMap, take, tap } from 'rxjs/operators';
import { PlainVersionedObject, VersionedObject, VersionedObjectConstructor } from '../base/versioned-object';
import { GatewayConnection } from '../data/gateway-connection';
import { Net } from '../data/net';
import { LogLevel } from '../diagnostics/log-level';
import { Logging } from '../diagnostics/logging';
import { Strings } from '../generated/strings';
import { RpcForwardResponse } from '../rpc/forward/rpc-forward-model';
import { Rpc } from '../rpc/rpc';
import { RpcRelationshipType } from '../rpc/rpc-base';
import { RpcServiceForwarder } from '../rpc/rpc-forwarder';
import { Connection, connectionTypeConstants, ConnectionUtility } from './connection';
import { CommonConnectionSettings } from './connection-manager-settings/common-connection-settings';
import { ConnectionSettings } from './connection-manager-settings/connection-settings';

export interface ConnectionManagerInitProperties {
    connections: Connection[];
    activeConnection: Connection;
}

export enum ConnectionChangeType {
    Initialized = 0,
    Activated = 1,
    Added = 2,
    Removed = 3
}

export interface ConnectionsChangedEvent {
    type: ConnectionChangeType;
}
export interface ConnectionChangedEvent extends ConnectionsChangedEvent {
    connection: Connection;
}

export interface ConnectionsInitializedEvent extends ConnectionsChangedEvent {
    connections: Connection[];
}

/**
 * Node aliases visit list.
 *  This is created when retry connection to aliases while connection.name not reacheable;
 *  and is deleted when find the reacheable alias or end of the list
 */
export interface AliasesVisitList {
    /**
     * current visit index
     */
    currentIndex: number;
    /**
     * aliases list
     */
    aliases: string[];
}

export class ConnectionManager extends RpcServiceForwarder implements ConnectionManagerInitProperties {
    private static activeConnectionPropertyName = 'activeConnection';
    private static connectionsPropertyName = 'connections';
    private static saveConnectionMethodName = 'saveConnection';
    private static saveConnectionsMethodName = 'saveConnections';
    private static removeConnectionMethodName = 'removeConnection';

    private static gatewayConnectionApi = 'connections';

    private allConnections: Connection[] = [];
    private activeConnectionIndex = -1;

    /**
     * The map of active nodes aliases list, this will be build on demand and clear up whenever any connection aliases changed
     * key: nodeName, this is unique, that means same nodeName with different connection type treat as one entry
     * value: A aliases list for this nodeName (say cluster name),
     *  this may contains child aliases (say IP address) of any alias (say server node).
     */
    private connectionAliasesMap: MsftSme.StringMap<string[]> = {};

    /**
     * nodeAliasesVisit map, the key is connection nodeName
     */
    private nodeAliasesVisitMap: MsftSme.StringMap<AliasesVisitList> = {};

    /**
     * Subject that Fires once and remembers when connections have been initialized
     */
    public connectionsInitialized = new ReplaySubject<Connection[]>(1);

    /**
     * Event subject that signals that the connection(s) have changed.
     * Filter on changeType to determine what type of change has occurred
     */
    public connectionsChanged = new Subject<ConnectionsChangedEvent>();

    /**
     * Indicates that restoring connections has started and shouldnt call the gateway again
     */
    private restoreInProgress = false;

    /**
     * Indicates that restoring connections has started and shouldnt call the gateway again
     */
    private restoreCompleted = false;

    /**
     * The connection settings subject for caching the connection's settings while active.
     */
    private connectionSettings = new ReplaySubject<ConnectionSettings>(1);

    constructor(rpc: Rpc, private gatewayConnection: GatewayConnection) {
        super('connection-manager', rpc);
    }

    public restoreConnections(refresh = false): Observable<Connection[]> {
        if (!this.canForward(RpcRelationshipType.Parent) && (refresh || (!this.restoreCompleted && !this.restoreInProgress))) {
            this.restoreInProgress = true;
            this.gatewayConnection.get(ConnectionManager.gatewayConnectionApi)
                .pipe(
                    map(data => {
                        if (data.value) {
                            (Array.isArray(data.value) ? data.value : [data.value])
                                .forEach(connection => {
                                    // for some reason the node api returns properties in a nested format. Unpack it into the correct format
                                    if (connection.properties) {
                                        connection = connection.properties;
                                    }

                                    // ensure bare minimum properties exist, ignore otherwise
                                    if (connection && connection.name && connection.type && connection.id) {
                                        this.addOrUpdateConnection(connection, false);
                                        return;
                                    }
                                });
                        }

                        return this.allConnections;
                    }),
                    catchError(error => {
                        this.restoreInProgress = false;
                        const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.ServerListRetrieve.message;
                        Logging.log({
                            source: 'ConnectionManager',
                            level: LogLevel.Error,
                            message: message.format(Net.getErrorMessage(error))
                        });
                        this.connectionsInitialized.error(error);
                        return of(this.allConnections);
                    }))
                .subscribe(() => {
                    this.forwardNotify(RpcRelationshipType.Child, ConnectionManager.connectionsPropertyName, this.allConnections);

                    this.connectionsChanged.next(
                        <ConnectionsInitializedEvent>{
                            type: ConnectionChangeType.Initialized,
                            connections: this.allConnections
                        });

                    this.connectionsInitialized.next(this.allConnections);
                    this.restoreInProgress = false;
                    this.restoreCompleted = true;
                });
        }

        return this.connectionsInitialized;
    }

    /**
     * Gets all connections
     */
    public get connections(): Connection[] {
        return this.allConnections;
    }

    /**
     * Gets active connection.
     */
    public get activeConnection(): Connection {
        return this.allConnections[this.activeConnectionIndex];
    }

    /**
     * Sets active connection.
     */
    public set activeConnection(connection: Connection) {
        // only change active connection if it has really changed
        if (!ConnectionUtility.areEqual(this.activeConnection, connection)) {
            if (!connection) {
                this.activeConnectionIndex = -1;
            } else {
                this.activeConnectionIndex = this.addOrUpdateConnection(connection, false);
            }

            this.connectionsChanged.next(<ConnectionChangedEvent>{ type: ConnectionChangeType.Activated, connection: connection });
            this.forwardNotify(RpcRelationshipType.Child, ConnectionManager.activeConnectionPropertyName, this.activeConnection);
            this.forwardNotify(RpcRelationshipType.Parent, ConnectionManager.activeConnectionPropertyName, this.activeConnection);
            // collect connection settings for new active connection
            this.getConnectionSettings().pipe(take(1)).subscribe(settings => this.connectionSettings.next(settings));
        }
    }

    /**
     * Add or update connection.
     */
    public addOrUpdateConnection(connection: Connection, save: boolean = true, merge = false): number {
        connection.id = ConnectionUtility.createConnectionId(connection.type, connection.name, connection.groupId);
        ConnectionUtility.forceLowercase(connection);
        let index = this.allConnections.findIndex(c => ConnectionUtility.areEqual(c, connection));
        if (index >= 0) {
            if (merge) {
                const oldConnection = this.allConnections[index];
                if (oldConnection.tags) {
                    connection.tags = oldConnection.tags.concat(connection.tags || []).unique();
                }
                if (oldConnection.properties) {
                    connection.properties = { ...oldConnection.properties, ...connection.properties || {} };
                }
            }

            this.allConnections[index] = connection;
            this.connectionsChanged.next(<ConnectionChangedEvent>{ type: ConnectionChangeType.Added, connection: connection });
        } else {
            this.allConnections.push(connection);
            this.connectionsChanged.next(<ConnectionChangedEvent>{ type: ConnectionChangeType.Added, connection: connection });
            if (!this.restoreInProgress) {
                this.connectionsInitialized.next(this.allConnections);
            }
            index = this.allConnections.length - 1;
            // notify parent and child of collection changed.
            this.forwardNotify(RpcRelationshipType.Parent, ConnectionManager.connectionsPropertyName, this.allConnections);
            this.forwardNotify(RpcRelationshipType.Child, ConnectionManager.connectionsPropertyName, this.allConnections);
        }

        if (save) {
            this.saveConnection(connection).subscribe();
        }

        return index;
    }

    /**
     * Remove connection.
     */
    public removeConnection(connection: Connection): Observable<any> {
        const forward = this.forwardExecute(RpcRelationshipType.Parent, ConnectionManager.removeConnectionMethodName, [connection]);
        if (forward) {
            return <Observable<any>>forward;
        }

        const urlEncodedID = encodeURIComponent(connection.id);
        return this.gatewayConnection.delete(`${ConnectionManager.gatewayConnectionApi}/${urlEncodedID}`)
            .pipe(
                map(response => {
                    const index = this.allConnections.findIndex(c => ConnectionUtility.areEqual(c, connection));
                    if (index >= 0) {
                        // if this connection is active, set active connection to null
                        if (this.activeConnectionIndex === index) {
                            this.activeConnection = null;
                        }

                        // remove the connection from all connections
                        this.allConnections.splice(index, 1);
                    }
                    this.connectionsChanged.next(<ConnectionChangedEvent>{ type: ConnectionChangeType.Removed, connection: connection });
                    if (!this.restoreInProgress) {
                        this.connectionsInitialized.next(this.allConnections);
                    }

                    // notify our children that connections have changed
                    this.forwardNotify(RpcRelationshipType.Child, ConnectionManager.connectionsPropertyName, this.allConnections);

                    return response;
                }));
    }

    public updateConnectionsLastCheckedTime(connections: Connection[]): Observable<any> {
        const now = Date.now();
        const observables = [];
        if (connections && connections.length > 0) {
            connections.forEach(connection => {
                if (connection.properties == null
                    || connection.properties.lastUpdatedTime == null
                    || MsftSme.round(connection.properties.lastUpdatedTime / 1000) + 2 < MsftSme.round(now / 1000)) {
                    // update if there is more than 2 second difference.
                    connection.properties = connection.properties || {};
                    connection.properties.lastUpdatedTime = now;
                    observables.push(this.saveConnection(connection));
                }

                observables.push(of(null));
            });
        } else {
            observables.push(of(null));
        }

        return concat(...observables);
    }

    public saveConnection(connection: Connection): Observable<any> {
        ConnectionUtility.forceLowercase(connection);
        const forward = this.forwardExecute(RpcRelationshipType.Parent, ConnectionManager.saveConnectionMethodName, [connection]);
        if (forward) {
            return <Observable<any>>forward;
        }

        if (!connection.type || !connection.name) {
            const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.ServerListFailedSave.message;
            return throwError(() => new Error(message));
        }

        connection.id = ConnectionUtility.createConnectionId(connection.type, connection.name, connection.groupId);

        // define properties if it doesn't exist on the connection
        if (MsftSme.isNullOrUndefined(connection.properties)) {
            connection.properties = {};
        }
        this.addOrUpdateConnection(connection, false);

        const urlEncodedID = encodeURIComponent(connection.id);
        return this.gatewayConnection.put(`${ConnectionManager.gatewayConnectionApi}/${urlEncodedID}`, JSON.stringify(connection))
            .pipe(
                tap((data: any) => {
                    if (data.properties && data.properties.displayName) {
                        // merge the display name from the gateway into the connection properties
                        connection.properties.displayName = data.properties.displayName;
                    }

                    this.forwardNotify(RpcRelationshipType.Child, ConnectionManager.connectionsPropertyName, this.allConnections);
                }));
    }

    /**
     * Bulk operation for saving multiple connections
     * @param connection the connection object.
     */
    public saveConnections(connections: Connection[]): Observable<any> {
        connections.forEach(connection => {
            this.addOrUpdateConnection(connection, false);
            ConnectionUtility.forceLowercase(connection);

            if (!connection.type || !connection.name) {
                const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.ServerListFailedSave.message;
                return throwError(() => new Error(message));
            }

            connection.id = ConnectionUtility.createConnectionId(connection.type, connection.name, connection.groupId);
        });

        const forward = this.forwardExecute(RpcRelationshipType.Parent, ConnectionManager.saveConnectionsMethodName, [connections]);
        if (forward) {
            return <Observable<any>>forward;
        }

        return this.gatewayConnection.put(`${ConnectionManager.gatewayConnectionApi}`, JSON.stringify(connections))
            .pipe(
                tap(_ => {
                    this.forwardNotify(RpcRelationshipType.Child, ConnectionManager.connectionsPropertyName, this.allConnections);
                }));
    }

    /**
     * Finds a connection given a name and type
     * @param name the name of the connection to find
     * @param type the type of the connection to find, defaults to server type
     */
    public findConnection(name: string, type?: string): Observable<Connection> {
        if (!name) {
            return of(null);
        }

        type = type || connectionTypeConstants.server;

        if (this.activeConnection && this.activeConnection.name === name && this.activeConnection.type === type) {
            return of(this.activeConnection);
        }

        return this.connectionsInitialized
            .pipe(
                map(_ => {
                    return this.connections.find(c => c.name === name && c.type === type);
                }));
    }

    /**
     * Called on a child service instance when onForwardInit returns from the parent
     * @param data The response from the forwardInit call
     */
    protected onForwardInitResponse(data: RpcForwardResponse<ConnectionManagerInitProperties>): void {
        if (data.error) {
            // if there is an error, we cannot continue, so throw its
            throw data.error;
        }

        this.allConnections = data.result.connections;
        this.activeConnection = data.result.activeConnection;
        this.connectionsChanged.next(
            <ConnectionsInitializedEvent>{
                type: ConnectionChangeType.Initialized,
                connections: this.allConnections
            });
        this.connectionsInitialized.next(this.allConnections);
    }

    /**
     * Called when a new instance of the service in another window is initialized and needs to synchronize with its parent
     * @param from The RpcRelationshipType that this request is from
     * @returns an observable for the all the values needed to initialize the service
     */
    protected onForwardInit(): Observable<ConnectionManagerInitProperties> {
        return of({
            connections: this.connections,
            activeConnection: this.activeConnection
        });
    }

    /**
     * Called when the forwarded services counterpart wants to get data from the parent
     * @param from The RpcRelationshipType that this request is from
     * @param name The name of the method to forward to
     * @param args The arguments of the method
     * @returns an observable for the result of the method call
     */
    protected onForwardExecute(from: RpcRelationshipType, name: string, args: any[]): Observable<any> {
        if (from === RpcRelationshipType.Child && args && args.length >= 1) {
            if (name === ConnectionManager.saveConnectionMethodName) {
                // we dont actually have anything to return here.
                return this.saveConnection(args[0]).pipe(map(() => null));
            }
            if (name === ConnectionManager.saveConnectionsMethodName) {
                return this.saveConnections(args[0]);
            }
            if (name === ConnectionManager.removeConnectionMethodName) {
                // we dont actually have anything to return here.
                return this.removeConnection(args[0]).pipe(map(() => null));
            }
        }
        // ConnectionManager does not allow any method calls at this time
        return this.nameNotFound(name);
    }

    /**
     * Called when the forwarded services counterpart sends a notify message
     * @param from The RpcRelationshipType that this request is from
     * @param name The name of the property to change
     * @param value The new value of the property
     * @returns an observable that completes when the property has been changed.
     */
    protected onForwardNotify(from: RpcRelationshipType, name: string, value: any): Observable<void> {
        if (name === ConnectionManager.connectionsPropertyName) {
            this.allConnections = value;
            this.connectionsChanged.next(
                <ConnectionsInitializedEvent>{
                    type: ConnectionChangeType.Initialized,
                    connections: this.allConnections
                });
            this.connectionsInitialized.next(this.allConnections);
            return of(null);
        }
        if (name === ConnectionManager.activeConnectionPropertyName) {
            this.activeConnection = value;
            return of(null);
        }
        return this.nameNotFound(name);
    }

    /**
     * Get aliases data and save the change with connection
     * @param aliases the alias list.
     * @param connection the connection object.
     * @param nodeName the node name.
     */
    public saveAliasesData(aliases: string[], connection: Connection, nodeName: string) {
        let save = false;
        // save the connection if aliase info changed
        if (!this.isArraySame(aliases, connection.aliases)) {
            connection.aliases = aliases;
            // remove the activeAlias if it's not in aliases any more
            if (connection.aliases.indexOf(connection.activeAlias) === -1) {
                connection.activeAlias = null;
            }
            save = true;
        }
        // if current nodeName is not connection name save it as activeAlias
        if (connection.name !== nodeName && connection.activeAlias !== nodeName) {
            connection.activeAlias = nodeName;
            save = true;
        } else {
            // current nodeName is connection name, remove connection activeAlias
            if (connection.name === nodeName && !!connection.activeAlias) {
                connection.activeAlias = null;
                save = true;
            }
        }

        if (save) {
            this.saveConnection(connection);
        }

        // delete visit map entry when succeed
        this.deleteAliasesVisitList(connection.name);
    }

    /**
     * Get active alias from nodeAliasesVisit map with given nodeName
     *  return the node in aliases list in order,
     *  return null if no alias or end of list
     * @param nodeName key in nodeAliasesVisitMap
     */
    public getActiveAlias(nodeName: string): string {
        // aliases visit list already exists
        if (nodeName in this.nodeAliasesVisitMap) {
            const vlist = this.nodeAliasesVisitMap[nodeName];
            // move currentIndex to next
            if (++vlist.currentIndex < vlist.aliases.length) {
                return vlist.aliases[vlist.currentIndex];
            } else {
                // end of the list
                this.deleteAliasesVisitList(nodeName);
                return null;
            }
        } else { // no aliases list yet (first time or none)
            const aliases = this.getNodeAliasesList(nodeName);
            if (aliases) { // create aliases visit list for the first time
                const vlist: AliasesVisitList = {
                    currentIndex: 0,
                    aliases: aliases
                };
                this.nodeAliasesVisitMap[nodeName] = vlist;
                // return first item, aliases[vlist.currentIndex]
                return aliases[0];
            } else {
                // no aliases, return null
                return null;
            }
        }
    }

    /**
     * Delete aliasesVistList entry with given nodeName from nodeAliasesVisitMap
     * @param nodeName the node name.
     */
    private deleteAliasesVisitList(nodeName: string): void {
        if (nodeName in this.nodeAliasesVisitMap) {
            delete this.nodeAliasesVisitMap[nodeName];
        }
    }

    private isArraySame(array1: string[], array2: string[]): boolean {
        // both are not null, compare every element
        array1 = [].concat(array1);
        array2 = [].concat(array2);
        if (array1 && array2) {
            return (array1.length === array2.length) && array1.every(function (element, index) {
                return element === array2[index];
            });
        } else {
            // both are null
            if (!array1 && !array2) {
                return true;
            } else { // one of it is null
                return false;
            }
        }
    }

    /**
     * Get nodeAliasesList from map; if not exists create it
     * @param nodeName name of the node, this is unique regardless connection type
     */
    private getNodeAliasesList(nodeName: string): string[] {
        if (nodeName in this.connectionAliasesMap) {
            return this.connectionAliasesMap[nodeName];
        } else {
            return this.buildNodeAliasesList(nodeName);
        }
    }

    /**
     * Build nodeAliasesList entry with given nodeName, then add it into the map,
     *  return the list of aliases
     * @param nodeName name of the node
     */
    private buildNodeAliasesList(nodeName: string): string[] {
        const connection = this.findConnectionWithAliases(nodeName);
        if (!connection) {
            return null;
        }
        // assume activeAlias is in aliases[]
        let aliases: string[] = [];
        if (connection.activeAlias) {
            // put activeAlias at the first of the list
            aliases.push(connection.activeAlias);
            aliases = aliases.concat(connection.aliases.filter(item => item !== connection.activeAlias));
        } else {
            aliases = connection.aliases;
        }
        for (let i = 0; i < aliases.length; i++) {
            const childList = this.buildNodeAliasesList(aliases[i]);
            // insert the childList to list, the list.length will increase
            if (childList) {
                // remove remaining items after current item
                const remainList = aliases.splice(i + 1, aliases.length - i);
                aliases = aliases.concat(childList, remainList);
                // next iteration move to node in childList
            }
        }
        this.connectionAliasesMap[nodeName] = aliases;
        return aliases;
    }

    /**
     * Finds the first connection with aliases info given a name, assume the connections already initialized
     * @param name the name of the connection to find
     */
    private findConnectionWithAliases(name: string): Connection {
        if (!name) {
            return null;
        }

        if (this.activeConnection && this.activeConnection.name === name && !!this.activeConnection.aliases) {
            return this.activeConnection;
        }

        return this.connections.find(c => c.name === name && !!(c.aliases));
    }

    /**
     * Gets the common connection settings.
     * By default, will use the active connection, but allows input for different connection objects.
     * @return Observable of the common connection settings object
     */
    public getCommonConnectionSettings(connection?: Connection): Observable<CommonConnectionSettings> {
        if (connection) {
            return this.getConnectionSettings(connection)
                .pipe(
                    take(1),
                    map(settings => settings.common),
                    take(1));

        } else {
            return this.connectionSettings.pipe(map(settings => settings.common), take(1));
        }
    }

    /**
     * Get extension connection settings for the active connection.
     * @return Observable of specified type
     */
    public getExtensionConnectionSettings<T extends VersionedObject>(type: VersionedObjectConstructor<T>): Observable<T> {
        const name = MsftSme.self().Environment.name;
        return this.connectionSettings
            .pipe(
                map(settings => VersionedObject.ensureIsVersionedObject(settings.extensions[name])),
                map(settings => {
                    return new type(
                        settings,
                        { save: (object) => this.setExtensionSettings(name, object) });
                }),
                take(1));
    }

    /**
     * Sets extension settings for the active connection.
     * @param extensionName the extension name.
     * @param extensionSettings the extension settings.
     */
    public setExtensionSettings(extensionName: string, extensionSettings: PlainVersionedObject): Observable<void> {
        return this.connectionSettings
            .pipe(mergeMap(settings => settings.trySave(() => {
                settings.extensions[extensionName] = extensionSettings;
            })));

    }

    /**
     * Gets the connection settings object
     * By default, will use the active connection, but allows input for different connection objects.
     * @return Observable of ConnectionSettings
     */
    private getConnectionSettings(connection = this.activeConnection): Observable<ConnectionSettings> {
        return of(connection).pipe(
            map(connectionObject => {
                return connectionObject.settings;
            }),
            take(1),
            catchError((error) => {
                const messageFormat = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Errors.UserProfile.Get.formatMessage;
                Logging.logError('ConnectionManager.getConnectionSettings', messageFormat.format(Net.getErrorMessage(error)));
                return throwError(() => error);
            }),
            map((settings: PlainVersionedObject) => {
                // If the setttings are not versioned (or not defined), then start with empty settings object
                settings = VersionedObject.ensureIsVersionedObject(settings);

                // return new connection settings object
                return new ConnectionSettings(settings, {
                    save: (object) => this.setConnectionSettings(object, connection)
                });
            }));
    }

    /**
     * Sets the connection settings from the active connection
     * @param settings a PlainVersionedObject
     * @return An observable with the result from the set operation
     */
    private setConnectionSettings(settings: PlainVersionedObject, connection: Connection): Observable<any> {
        // return an observable that saves the connection
        connection.settings = settings;
        return this.saveConnection(connection);
    }

}
